import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock } from 'lucide-react';
import { Button } from '../components/Button';
import { useT } from '../i18n';

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.title ?? body.message ?? t('a-login.invalidCredentials'));
      }

      const data = await res.json();
      localStorage.setItem('rsvs.access', data.access_token ?? data.accessToken);
      localStorage.setItem('rsvs.refresh', data.refresh_token ?? data.refreshToken ?? '');
      localStorage.setItem('rsvs.user', JSON.stringify(data.user ?? {}));
      if (data.tenant?.slug) localStorage.setItem('rsvs.tenant_slug', data.tenant.slug);
      else localStorage.removeItem('rsvs.tenant_slug');
      if (data.tenant?.name) localStorage.setItem('rsvs.tenant_name', data.tenant.name);

      navigate('/dashboard');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-brand-500 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Minha Reserva</h1>
          <p className="text-ink-subtle text-sm mt-1">{t('a-login.subtitle')}</p>
        </div>

        <div className="bg-surface-panel rounded-xl border border-line shadow-lg p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="label">{t('a-login.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input
                  id="login-email"
                  className="input pl-9"
                  type="email"
                  placeholder={t('a-login.emailPlaceholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="label">{t('a-login.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input
                  id="login-password"
                  className="input pl-9"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" loading={loading} className="w-full justify-center py-2 text-base mt-2">
              {loading ? t('a-login.submitting') : t('a-login.submit')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-muted">
            {t('a-login.noAccount')}{' '}
            <Link to="/signup" className="text-brand-700 font-semibold hover:text-brand-800">
              {t('a-login.createNow')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
